import { Link, useParams } from "react-router-dom";
import { studentIdsForAccount } from "../domain/auth";
import {
  eventAudienceLabel,
  eventDescription,
  eventHiddenFromFamilies,
  studentsOpenForEvent,
  type LiftEvent,
} from "../domain/data";
import { useAuth } from "../state/AuthProvider";
import { useEvents } from "../state/events";
import { useLocale } from "../state/i18n";
import { useStudents } from "../state/students";
import {
  EventDescriptionBlock,
  EventSeatSummary,
  PrototypeBanner,
  PublicNav,
} from "../components/ui";

function SignedInActions({ event }: { event: LiftEvent }) {
  const { account } = useAuth();
  const { studentsByIds } = useStudents();
  const { t } = useLocale();
  if (!account) return null;
  const name = `${account.given} ${account.family}`.trim();

  if (account.role === "staff") {
    return (
      <>
        <div className="wire dashed" style={{ marginTop: "1rem" }}>
          <div className="meta">{t("events.signedInAs", { name })}</div>
          <p style={{ margin: "0.5rem 0 0" }}>{t("events.signedInStaff")}</p>
        </div>
        <div className="actions">
          <Link className="btn" to={`/staff/events/${event.id}`}>
            {t("events.openStaff")}
          </Link>
        </div>
      </>
    );
  }

  const ids = studentIdsForAccount(account);
  const kids = studentsByIds(ids);
  if (ids.length === 0) {
    return (
      <>
        <div className="wire dashed" style={{ marginTop: "1rem" }}>
          <div className="meta">{t("events.signedInAs", { name })}</div>
          <p style={{ margin: "0.5rem 0 0" }}>{t("events.signedInNeedForm")}</p>
        </div>
        <div className="actions">
          <Link className="btn" to="/register">
            {t("events.fillForm")}
          </Link>
        </div>
      </>
    );
  }

  const eligible = studentsOpenForEvent(event, kids);
  if (eligible.length === 0) return null;

  return (
    <>
      <div className="wire dashed" style={{ marginTop: "1rem" }}>
        <div className="meta">{t("events.signedInAs", { name })}</div>
        <p style={{ margin: "0.5rem 0 0" }}>{t("events.signedInRsvp")}</p>
      </div>
      <div className="actions">
        {eligible.map((student) => (
          <Link
            key={student.id}
            className="btn"
            to={`/student/${student.id}/events/${event.id}`}
          >
            {t("events.rsvpFor", { name: `${student.given} ${student.family}` })}
          </Link>
        ))}
        {account.role === "parent" ? (
          <Link className="btn ghost" to="/register">
            {t("events.addStudent")}
          </Link>
        ) : null}
      </div>
    </>
  );
}

export function PublicEvent() {
  const { eventId = "" } = useParams();
  const { getEvent, countsFor } = useEvents();
  const { account, session } = useAuth();
  const { locale, t } = useLocale();
  const event = getEvent(eventId);

  if (!event || eventHiddenFromFamilies(event)) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <PublicNav />
          <p>{t("events.notFound")}</p>
          <Link to="/events">{t("events.back")}</Link>
        </main>
      </div>
    );
  }

  const counts = countsFor(event.id);

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <PublicNav />
        <p className="meta">{t("events.publicMetaSingle")}</p>
        <h1>{event.name}</h1>
        <p className="lede">
          {event.dateLabel} · {event.timeLabel}
          <br />
          {event.location}
        </p>

        <EventSeatSummary
          registered={counts.registered}
          capacity={event.capacity}
          remaining={counts.remaining}
          waitlisted={counts.waitlisted}
          waitlistEnabled={event.waitlistEnabled}
          audience={eventAudienceLabel(event)}
        />

        <EventDescriptionBlock
          label={t("events.about")}
          text={eventDescription(event, locale)}
        />

        {session && account ? (
          <SignedInActions event={event} />
        ) : (
          <>
            <div className="wire dashed" style={{ marginTop: "1rem" }}>
              <div className="meta">{t("events.alreadyForm")}</div>
              <p style={{ margin: "0.5rem 0 0" }}>{t("events.alreadyFormBody")}</p>
            </div>
            <div className="actions">
              <Link className="btn" to="/signin">
                {t("events.signInRsvp")}
              </Link>
              <Link className="btn ghost" to="/register">
                {t("events.newForm")}
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
